import axios from 'axios';
import { AuthToken } from './types/authentication/AuthToken';
import { AuthTokenService } from './services';

interface Authentication {
  user: {
    id?: number;
  };
  updateToken(token: AuthToken): void;
}

const US_USER_AUTH_TOKEN = 'US_USER_AUTH_TOKEN';
const US_USER_REFRESH_TOKEN = 'US_USER_REFRESH_TOKEN';

// On a 401 with no refresh path, clear auth and bounce to login. Mirrors
// Angular's rejectResponseAndLogout (apps/angular/src/js/apps/app.js:1862-1868)
// and matches the bootstrap flow in apps/react/src/dependencies/Environment.ts:64
// so the login page receives `?redirectTo=` and routes the user back after auth.
const redirectToLogin = (): void => {
  if (typeof window === 'undefined') return;
  const here = window.location.pathname + window.location.search;
  window.location.href = `/login?redirectTo=${encodeURIComponent(here)}`;
};

const initBearerToken = (): void => {
  axios.interceptors.request.use(
    async config => {
      const token = localStorage.getItem(US_USER_AUTH_TOKEN);
      if (
        config.url?.indexOf('/v2/public/auth-token') === -1 &&
        config.headers &&
        token
      ) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (config.headers) {
        config.headers.Authorization = '';
      }

      return config;
    },
    async error => Promise.reject(error)
  );
};

let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processServicesQueue = () => {
  failedQueue.forEach(prom => {
    prom.resolve();
  });

  failedQueue = [];
};

let lastRefreshRequest: number;
const requestTimeLimit = 5000;

const canRefresh = (): boolean => (
  lastRefreshRequest === null ||
  lastRefreshRequest === undefined ||
  lastRefreshRequest + requestTimeLimit < Date.now()
);

const setServicesLastRefreshRequest = (servicesLastRefreshRequest: number) => {
  lastRefreshRequest = servicesLastRefreshRequest;
};

const initResponseInterceptor = (
  authentication: Authentication,
  processAppQueues?: () => void
) => {
  axios.interceptors.response.use(
    response => response,
    async error => {
      const refreshToken = localStorage.getItem(US_USER_REFRESH_TOKEN);
      if (error.response?.status === 401) {
        if (!refreshToken) {
          authentication.updateToken({});
          redirectToLogin();
          return Promise.reject(error);
        }
        if (canRefresh()) {
          lastRefreshRequest = Date.now();
          const authToken: AuthToken = {
            userId: authentication.user.id,
            refreshToken: {
              token: refreshToken,
            },
          };

          try {
            const authResponse = await AuthTokenService.create(authToken);
            const responseData = authResponse.data.data;

            authentication.updateToken(responseData);
            processServicesQueue();
            if (processAppQueues) {
              processAppQueues();
            }

            return await axios.request(error.config);
          } catch {
            authentication.updateToken({});
            redirectToLogin();
            return Promise.reject(error);
          }
        } else {
          try {
            await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            const token = localStorage.getItem(US_USER_AUTH_TOKEN);

            error.config.headers.Authorization = `Bearer ${token}`;
            return await axios(error.config);
          } catch (err) {
            return Promise.reject(err);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

export {
  canRefresh,
  initBearerToken,
  initResponseInterceptor,
  processServicesQueue,
  setServicesLastRefreshRequest,
};
export * from './constants';
export * from './services';
export * from './types';
export * from './utils';
